import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Icon } from './icons';
import { Img } from './Img';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface GraduationStatus {
  hasSuggestion: boolean;
  matchId?: string;
  otherUserName?: string;
  otherUserPhoto?: string | null;
}

interface GraduationPromptProps {
  t: Theme;
}

export function GraduationPrompt({ t }: GraduationPromptProps) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState<'idle' | 'waiting' | 'coupled'>('idle');

  const { data } = useQuery<GraduationStatus>({
    queryKey: ['graduation-status'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/pending-graduation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { hasSuggestion: false };
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const graduateMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/graduate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ matchId }),
      });
      if (!res.ok) throw new Error('Failed to graduate');
      return res.json() as Promise<{ waiting?: boolean; coupled?: boolean; coupleId?: string }>;
    },
    onSuccess: (result) => {
      if (result.coupled) {
        setState('coupled');
        queryClient.invalidateQueries({ queryKey: ['graduation-status'] });
        queryClient.invalidateQueries({ queryKey: ['couple-me'] });
      } else {
        setState('waiting');
      }
    },
  });

  if (dismissed || !data?.hasSuggestion) return null;

  const { matchId, otherUserName, otherUserPhoto } = data;
  const firstName = otherUserName?.split(' ')[0] ?? 'your match';

  // After coupled: success state briefly before dismissing
  if (state === 'coupled') {
    return (
      <div style={{
        margin: '0 20px 16px',
        padding: '18px 20px',
        borderRadius: 18,
        background: `linear-gradient(135deg, ${t.gold}22, ${t.gold}08)`,
        border: `1px solid ${t.gold}55`,
      }}>
        <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, marginBottom: 6 }}>
          You're a couple
        </div>
        <div style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkSoft, lineHeight: 1.5, marginBottom: 14 }}>
          You and {otherUserName} both said yes. Head to <strong style={{ color: t.ink }}>You</strong> to set up your couple profile.
        </div>
        <button
          onClick={() => setDismissed(true)}
          style={{
            all: 'unset', cursor: 'pointer', fontFamily: FONTS.sans, fontSize: 13,
            fontWeight: 600, color: t.gold, padding: '8px 16px', borderRadius: 100,
            background: `${t.gold}18`, border: `1px solid ${t.gold}44`,
          }}
        >
          Got it →
        </button>
      </div>
    );
  }

  // Waiting state: one partner has said yes, waiting for the other
  if (state === 'waiting') {
    return (
      <div style={{
        margin: '0 20px 16px',
        padding: '16px 20px',
        borderRadius: 18,
        background: t.paperAlt,
        border: `1px solid ${t.line}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          background: `${t.gold}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${t.gold}`, borderTopColor: 'transparent', animation: 'spin 1.2s linear infinite' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13.5, fontWeight: 600, color: t.ink, marginBottom: 2 }}>
            Waiting for {firstName}
          </div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute }}>
            Private until you both decide — no pressure.
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          style={{ all: 'unset', cursor: 'pointer', width: 28, height: 28, borderRadius: '50%', background: t.card, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          {Icon.x({ s: 11, c: t.inkMute })}
        </button>
      </div>
    );
  }

  // Default: the graduation prompt
  return (
    <div style={{
      margin: '0 20px 16px',
      borderRadius: 20,
      overflow: 'hidden',
      background: t.card,
      border: `1px solid ${t.line}`,
      boxShadow: t.shadow,
    }}>
      {/* Header strip */}
      <div style={{
        padding: '14px 18px 12px',
        background: `linear-gradient(135deg, ${t.gold}18, ${t.blush}10)`,
        borderBottom: `1px solid ${t.line}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        {otherUserPhoto ? (
          <Img placeholder={t.paperAlt} src={otherUserPhoto} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${t.gold}44` }} />
        ) : (
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${t.gold}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontFamily: FONTS.display, fontSize: 18, color: t.gold }}>{firstName[0]}</span>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: t.gold, marginBottom: 3 }}>
            Liking where this is going?
          </div>
          <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.ink, lineHeight: 1.2 }}>
            You & {firstName} hit it off
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          style={{ all: 'unset', cursor: 'pointer', width: 28, height: 28, borderRadius: '50%', background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          {Icon.x({ s: 11, c: t.inkMute })}
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '14px 18px 16px' }}>
        <p style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkSoft, lineHeight: 1.55, margin: '0 0 14px' }}>
          Want to explore double dates as a two? You'd match with other couples and let AI plan a four-person night out.
        </p>

        <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, marginBottom: 14, letterSpacing: '0.03em' }}>
          Private until you both answer · reversible anytime
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => matchId && graduateMutation.mutate(matchId)}
            disabled={graduateMutation.isPending}
            style={{
              all: 'unset', cursor: graduateMutation.isPending ? 'default' : 'pointer',
              flex: 1, padding: '12px 0', borderRadius: 14, textAlign: 'center',
              fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
              background: graduateMutation.isPending ? t.paperAlt : t.gold,
              color: graduateMutation.isPending ? t.inkMute : '#fff',
              transition: 'all 150ms',
            }}
          >
            {graduateMutation.isPending ? 'Saving…' : "Yes, let's try"}
          </button>
          <button
            onClick={() => setDismissed(true)}
            style={{
              all: 'unset', cursor: 'pointer',
              flex: 1, padding: '12px 0', borderRadius: 14, textAlign: 'center',
              fontFamily: FONTS.sans, fontSize: 14, fontWeight: 500,
              background: t.paperAlt, color: t.inkSoft,
              border: `1px solid ${t.line}`,
            }}
          >
            Not yet
          </button>
        </div>
      </div>
    </div>
  );
}
